import clientStorage from '@Common/Core/ClientStorage';
import constants from '@Common/Core/Constant';
import { ApplicationType } from '@Common/Enums';
import { transform } from '@Common/Helpers/CollectionUtils';
import { UrlParameters, UrlParser } from '@Common/Helpers/UrlParser';
import { properties } from '../Core/Properties';
import * as commonUtils from './CommonUtils';
import { log } from './CommonUtils';
import localizationHelper from './LocalizationHelper';

namespace urlHelper {
    export type UrlParams = Partial<{
        applicationType: ApplicationType;
        projectId: Guid;
        viewId: Guid;
        viewName: string;
        bookmarkId: Guid;
        componentId: Guid;
        replaceViewer: boolean;
    }>;

    const mtConsolePage = 'MTConsole.aspx';

    export function getLocation(): Location {
        return window.location;
    }

    export function getHostUrl(): string {
        const location = urlHelper.getLocation();
        return location.protocol + '//' + location.hostname + (location.port ? ':' + location.port : '');
    }

    export function getBaseUrl(withoutEndingSlash = false): string {
        let baseUrl = urlHelper.getHostUrl();
        if (altosoft.baseAppPath) {
            baseUrl += altosoft.baseAppPath;
        } else {
            const location = urlHelper.getLocation();
            baseUrl += location.pathname.slice(0, location.pathname.lastIndexOf('/'));
            baseUrl = baseUrl.replace(new RegExp(mtConsolePage, 'ig'), '');
        }
        baseUrl = withoutEndingSlash ? removeSlash(baseUrl) : appendSlash(baseUrl);
        return baseUrl;
    }

    export function urlParamsMap(url?: string): UrlParameters {
        const { params } = UrlParser.parse(url || urlHelper.getLocation().href);
        return params;
    }

    export function urlParameters(url?: string): { key: string; value: string }[] {
        const params = urlHelper.urlParamsMap(url);
        const keyValues = transform(params.entries(), ([key, value]) => ({ key, value }));
        return Array.from(keyValues);
    }

    export function getUrlParameter(parameterName: string): string {
        return urlHelper.urlParamsMap().get(parameterName) ?? '';
    }

    export function refresh(newParameters?: { [key: string]: string }): void {
        urlHelper.openPage(urlHelper.getBaseUrl(), newParameters, true);
    }

    export function openPage(baseUrl: string, newParameters?: { [key: string]: string }, useCurrentParameters = false): void {
        const parameters = useCurrentParameters ? urlHelper.getLocation().search.substring(1).split('&') : [];

        if (newParameters) {
            _(newParameters).forEach((value, key) => {
                if (value !== undefined) {
                    const newParameter = key + '=' + value;
                    const oldIndex = parameters.getIndexOf(item => item.startsWith(key + '='));
                    if (oldIndex > -1) {
                        parameters[oldIndex] = newParameter;
                    } else {
                        parameters.push(newParameter);
                    }
                }
            });
        }

        if (parameters.length > 0) {
            baseUrl += '?' + parameters.join('&');
        }

        urlHelper.getLocation().assign(baseUrl);
    }

    export let canShowError: boolean = null;

    export function logout(sessionExpired = false): void {
        const parameters = {};
        if (altosoft.appId == ApplicationType.viewer) {
            parameters['sessionExpired'] = sessionExpired ? '1' : undefined;
            parameters['logoutUrl'] = altosoft.logoutUrl ? altosoft.logoutUrl : undefined;
        }
        urlHelper.openPage(urlHelper.getBaseUrl() + constants.logoutPage, parameters);
        canShowError = false;
    }

    export function getDataServiceUrl(): string {
        return urlHelper.getBaseUrl() + 'api/serviceHandler';
    }

    export function getConditionalImageUrl(imageName: string): string {
        return constants.imagesPath + constants.gridCellIconsImagesPath + imageName + '.png';
    }

    const extensions = /.(jpg|png|cur|gif|woff2|woff|svg)$/;

    export function getImageUrl(imageName: string): string {
        imageName = extensions.test(imageName) ? imageName : `${imageName}.png`;
        return `${constants.imagesPath}${imageName}?v=${altosoft.version}`;
    }

    export function downloadFile(fileId: Guid): void {
        const url = getFullDataServiceUrl('/raw/DownloadFile', { fileId });
        openUrl(url, true);
    }

    export function getFullDataServiceUrl(path: string, param?: Record<string, unknown>): string { 
        let url = urlHelper.getDataServiceUrl();
        url += path;

        const search = $.param(param, true);
        if (search) {
            url += '?' + search;
        }
        return url;
    }

    export function completeUrl(url: string): string {
        const lowerCase = url.toLowerCase();
        if (
            !lowerCase.startsWith('http://') &&
            !lowerCase.startsWith('https://') &&
            !lowerCase.startsWith('ftp://') &&
            !lowerCase.startsWith('ftps://') &&
            !lowerCase.startsWith('/')
        ) {
            return urlHelper.getLocation().protocol + '//' + url;
        }
        return url;
    }

    export function openUrl(url: string | PromiseLike<string>, download = false): void {
        if ((properties.isLocalApp || properties.isStandalone) && !commonUtils.isPromiseLike(url)) {
            const a = document.createElement('a');
            a.setAttribute('href', url);
            a.setAttribute('target', '_blank');
            const dispatch = document.createEvent('HTMLEvents');
            dispatch.initEvent('click', true, true);
            a.dispatchEvent(dispatch);
        } else {
            if (download && !commonUtils.isPromiseLike(url)) {
                properties.isDownloading = true;
                urlHelper.getLocation().href = url;
            } else {
                if (commonUtils.isPromiseLike(url)) {
                    url.then(openBlankPage, log);
                } else {
                    openBlankPage(url);
                }
            }
        }

        function openBlankPage(urlStr: string): void {
            window.open(urlHelper.completeUrl(urlStr), '_blank');
        }
    }

    export function getViewerBaseUrl(): string {
        const baseUrl = getApplicationBaseUrl(ApplicationType.viewer);
        return baseUrl;
    }

    export function getViewerUrl(params: UrlParams): string {
        const urlParams = $.param(_.pick<UrlParams>(params, 'viewId', 'viewName', 'projectId', 'bookmarkId'), true);
        const baseUrl = getViewerBaseUrl();
        return `${baseUrl}?${urlParams}`;
    }

    export function openViewer(params: UrlParams): void {
        const url = urlHelper.getViewerUrl(params);

        if (params.replaceViewer) {
            urlHelper.getLocation().href = url;
        } else {
            urlHelper.openUrl(url);
        }
    }

    export function openViewerHelp(): void {
        urlHelper.openHelp('Insight_viewer');
    }

    export function getHelpUrl(appPath: string, contextHelpId?: string): string {
        if (contextHelpId) {
            contextHelpId = _.defaultTo(_.get(constants.contextHelpIds, [appPath, contextHelpId]), contextHelpId);
        }
        const baseHelpPathWithLocale = altosoft.useOfflineHelp
            ? getBaseUrl() + '../' + constants.helpSettings.offlineFolder
            : constants.helpSettings.hostedUrl;
        const locale = localizationHelper.userCultureInfo.locale;
        const baseHelpPath = baseHelpPathWithLocale.format(constants.helpSettings.supportedLocales[locale] || constants.helpSettings.defaultLocale);
        const contextPage = contextHelpId ? `?cs=${contextHelpId}` : '';
        return `${baseHelpPath}${appPath}/index.html${contextPage}`;
    }

    export function openHelp(appPath: string, contextHelpId?: string): void {
        window.open(getHelpUrl(appPath, contextHelpId), '_blank');
    }

    export function openApp(newApp: ApplicationType, openInNewWindow?: boolean): boolean {
        const url = urlHelper.getApplicationUrl(newApp);
        if (openInNewWindow == null) {
            openInNewWindow = clientStorage.getGlobalSettings().openAppsInNewWindow;
        }
        if (openInNewWindow) {
            window.open(url, '_blank');
            return true;
        } else {
            urlHelper.getLocation().assign(url);
            return false;
        }
    }

    const getTruthAppType = (appId: ApplicationType): ApplicationType => {
        if (appId === ApplicationType.tenant) {
            return ApplicationType.admin;
        }
        return appId;
    };

    export function getApplicationBaseUrl(appId: ApplicationType) : string
    {
        const currentAppType = getTruthAppType(altosoft.appId);
        const newRealAppType = getTruthAppType(appId);
        const newAppName = urlHelper.getApplicationName(newRealAppType);

        const origBaseUrl = urlHelper.getBaseUrl(true);
        let baseUrl = origBaseUrl;
        if (appId !== currentAppType) {
            baseUrl = baseUrl.slice(0, baseUrl.lastIndexOf('/')) + '/' + newAppName + '/';
        }

        return baseUrl;
    }

    export function getApplicationUrl(appId: ApplicationType, additionParams: _.Dictionary<string> | string = ''): string {
        let baseUrl = getApplicationBaseUrl(appId);

        if (_.isPlainObject(additionParams)) {
            additionParams = _(additionParams as _.Dictionary<string>)
                .map((val: string, key: string) => `${key}=${encodeURIComponent(val)}`)
                .join('&');
        }
        if (appId === ApplicationType.tenant) {
            baseUrl += mtConsolePage;
        }
        if (additionParams) {
            baseUrl += baseUrl.indexOf('?') < 0 ? '?' : '&';
            baseUrl += additionParams;
        }
        return baseUrl;
    }

    export function getComponentUrl(params: UrlParams): string {
        return `${urlHelper.getViewerUrl(params)}&${constants.componentIdParameter}=${params.componentId}`;
    }

    export function checkAppSupport(): void {
        if (!urlHelper.isHTML5Supported()) {
            urlHelper.getLocation().href = `${urlHelper.getBaseUrl()}HtmlNotSupported`;
        }
    }

    export function isHTML5Supported(): boolean {
        const browser = properties.browser;
        return browser.webkit || browser.safari || browser.mozilla || (browser.msie && browser.version >= 9);
    }

    const userAppNames = constants.applicationNames;
    const appNames = {
        [ApplicationType.admin]: userAppNames.admin,
        [ApplicationType.themeManager]: userAppNames.themeManager,
        [ApplicationType.studio]: userAppNames.studio,
        [ApplicationType.dataLoadManager]: userAppNames.dataLoadManager,
        [ApplicationType.viewer]: userAppNames.viewer
    };

    export function getApplicationName(appId: ApplicationType): string {
        if (appId in appNames) {
            return appNames[appId];
        }
        throw new Error('Could not find action category. Unknown application id.');
    }

    export function isDebug(): boolean {
        return urlParamsMap().has('debug');
    }

    export function getCssImageUrl(image: string): string {
        return `url("${getImageUrl(image)}")`;
    }

    // eslint-disable-next-line no-inner-declarations
    function hasSlash(url: string): boolean {
        return url.length > 0 && url[url.length - 1] === '/';
    }

    export function appendSlash(url: string): string {
        return hasSlash(url) ? url : url + '/';
    }

    export function removeSlash(url: string): string {
        return hasSlash(url) ? url.slice(0, -1) : url;
    }
}

export default urlHelper;
